// App.jsx
import React, { useState, useEffect } from 'react';
import { Suspense } from 'react';

// Context Providers
import { AppProvider } from './context/AppContext';
import { ThemeProvider } from './context/ThemeContext';
import { ScrollProvider } from './context/ScrollContext';
import { ChatbotProvider } from './context/ChatbotContext';
import { NotificationProvider } from './context/NotificationContext';

// Layout Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ParticleBackground from './components/layout/ParticleBackground';
import ScrollIndicator from './components/layout/ScrollIndicator';

// Services & UI Components
import QChatbot from './components/services/QChatbot';
import NotificationCenter from './components/ui/NotificationCenter';

// Slide Components
import Slide1Portal from './components/slides/Slide1Portal';
import Slide2Equation from './components/slides/Slide2Equation';
import Slide3Leadership from './components/slides/Slide3Leadership';
import Slide4Services from './components/slides/Slide4Services';
import Slide5Team from './components/slides/Slide5Team';
import Slide6Legacy from './components/slides/Slide6Legacy';
import Slide7Recruitment from './components/slides/Slide7Recruitment';
import Slide8Interface from './components/slides/Slide8Interface';

// Styles
import './App.css';
import './index.css';
import './styles/animations.css';
import './styles/variables.css';

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-xl text-blue-500">Loading Quavrion...</div>
  </div>
);

// Simple page wrapper for modal-like views
const PageWrapper = ({ title, children, showBackButton = true }) => (
  <div className="min-h-screen pt-20 pb-20 flex items-center justify-center p-4">
    <div className="max-w-4xl w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
      <h1 className="text-4xl font-bold mb-6 text-center">{title}</h1>
      {children}
      {showBackButton && (
        <div className="mt-8 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Back to Home
          </button>
        </div>
      )}
    </div>
  </div>
);

function App() {
  // Simple state for current view
  const [currentView, setCurrentView] = useState('home');

  // Handle navigation
  const navigateTo = (view) => {
    setCurrentView(view);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Set up keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && currentView !== 'home') {
        setCurrentView('home');
      }
      if (e.key === '1') setCurrentView('home');
      if (e.key === '2') setCurrentView('services');
      if (e.key === '3') setCurrentView('team');
      if (e.key === '4') setCurrentView('careers');
      if (e.key === '5') setCurrentView('contact');
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentView]);

  return (
    <AppProvider>
      <ThemeProvider>
        <ScrollProvider>
          <ChatbotProvider>
            <NotificationProvider>
              
              
              <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-black">
                <ParticleBackground />
                <Navbar onNavigate={navigateTo} currentView={currentView} />
                
                <main className="relative">
                  <Suspense fallback={<LoadingFallback />}>
                    {currentView === 'home' && (
                      <div id="home-sections">
                        <Slide1Portal key="slide1" />
                        <Slide2Equation key="slide2" />
                        <Slide3Leadership key="slide3" />
                        <Slide4Services key="slide4" />
                        <Slide5Team key="slide5" />
                        <Slide6Legacy key="slide6" />
                        <Slide7Recruitment key="slide7" />
                        <Slide8Interface key="slide8" />
                      </div>
                    )}
                    
                    {currentView === 'services' && (
                      <PageWrapper title="Our Services">
                        <Slide4Services standaloneMode />
                      </PageWrapper>
                    )}
                    
                    {currentView === 'team' && (
                      <PageWrapper title="Our Team">
                        <Slide5Team standaloneMode />
                      </PageWrapper>
                    )}
                    
                    {currentView === 'careers' && (
                      <PageWrapper title="Careers">
                        <Slide7Recruitment standaloneMode />
                      </PageWrapper>
                    )}
                    
                    {currentView === 'contact' && (
                      <PageWrapper title="Contact Us">
                        <Slide8Interface standaloneMode />
                      </PageWrapper>
                    )}
                    
                    {currentView === 'login' && (
                      <PageWrapper title="Login">
                        <div className="space-y-4">
                          <p className="text-gray-600 dark:text-gray-400">
                            Login functionality coming soon...
                          </p>
                          <div className="max-w-md mx-auto space-y-4">
                            <input
                              type="email"
                              placeholder="Email"
                              className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                            />
                            <input
                              type="password"
                              placeholder="Password"
                              className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                            />
                            <button className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
                              Sign In
                            </button>
                          </div>
                        </div>
                      </PageWrapper>
                    )}
                    
                    {currentView === 'register' && (
                      <PageWrapper title="Register">
                        <p className="text-gray-600 dark:text-gray-400">
                          Registration coming soon...
                        </p>
                      </PageWrapper>
                    )}
                    
                    {currentView === 'profile' && (
                      <PageWrapper title="Your Profile">
                        <p className="text-gray-600 dark:text-gray-400">
                          Profile page coming soon...
                        </p>
                      </PageWrapper>
                    )}
                  </Suspense>
                </main>
                
                <Footer onNavigate={navigateTo} />
                {/* <ScrollIndicator /> */}
                <QChatbot />
                <NotificationCenter />
                
                {/* View Indicator (for debugging) */}
                {currentView !== 'home' && (
                  <div 
                    className="fixed bottom-4 right-4 z-40 bg-blue-500 text-white px-4 py-2 rounded-full shadow-lg cursor-pointer hover:bg-blue-600"
                    onClick={() => setCurrentView('home')}
                  >
                    ← Back to Home
                  </div>
                )}
              </div>
            </NotificationProvider>
          </ChatbotProvider>
        </ScrollProvider>
      </ThemeProvider>
    </AppProvider>
  );
}
  
export default App;